import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { map, distinctUntilChanged, catchError, filter, take } from 'rxjs/operators';

import { BaseActionService } from './base.action.service';
import { OrderSelectors, ApplicationState } from '../reducers';
import { OrderActions, OrderTransactionActions } from '../core/actions';
import { Order } from '../../objects/order';
import { LineItemRequest } from '../../objects/line-item-request';
import { BookingRequest } from '../../objects/booking-request';
import { Booking } from '../../objects/booking';
import { LineItem } from '../../objects/line-item';
import { Transaction } from '../../objects/transaction';
import { CustomerDetails } from 'src/objects/checkout-request';
import { StripeService } from './stripe.service';

import { CreateOrderPayload } from './actions/order.actions';
import { TipUpdatedEvent } from '../cart/cart-tip/cart-tip.component';

/**
 * DestinationActionService abstracts communication with Store
 */
export interface IOrderActionService {
  loadOrderByToken(token: string): void;
  loadCompletedOrderByNumber(orderNumber: string): void;
  createOrder(payload: CreateOrderPayload): void;
  getOrder(): Observable<Order | null>;
  getOrderError(): Observable<Error | null>;
  getCompletedOrder(): Observable<Order | null>;
}

export interface IOrderManagementActionService {
  addItem(payload: { request: LineItemRequest | BookingRequest }): void;
  removeItem(payload: {item: Booking|LineItem; order: Order}): void;
  getOrderIsItemBeingAdded(): Observable<boolean>;
  getOrderIsItemBeingRemoved(item: LineItem | Booking): Observable<boolean>;
  getOrderErrorProcessingItem(): Observable<Error | null>;
  getOrderLastAddedItem(): Observable<Booking | LineItem | null>;
  clearUpdatingTipError(): void;
}

@Injectable({
  providedIn: 'root'
})
export class OrderActionService
  extends BaseActionService<ApplicationState>
  implements IOrderActionService, IOrderManagementActionService {
  protected store: Store<ApplicationState>;
  private stripeService = inject(StripeService);


  constructor() {
    const store = inject<Store<ApplicationState>>(Store);

    super(store);

    this.store = store;
  }

  readonly order = this.selectSignal(OrderSelectors.getOrder);
  readonly orderError = this.selectSignal(OrderSelectors.getOrderError);
  readonly completedOrder = this.selectSignal(OrderSelectors.getCompletedOrder);
  readonly orderTimeout = this.selectSignal(OrderSelectors.getOrderTimeout);
  readonly orderIsItemBeingAdded = this.selectSignal(OrderSelectors.getOrderIsItemBeingAdded);
  readonly orderListOfItemsBeingDeleted = this.selectSignal(OrderSelectors.getOrderIsItemBeingRemoved);
  readonly orderErrorProcessingItem = this.selectSignal(OrderSelectors.getOrderErrorProcessingItem);
  readonly orderLastAddedItem = this.selectSignal(OrderSelectors.getOrderLastAddedItem);
  readonly updatingTip = this.selectSignal(OrderSelectors.getOrderUpdatingTip);
  readonly updatingTipError = this.selectSignal(OrderSelectors.getOrderUpdatingTipError);
  readonly submittingPromoCode = this.selectSignal(OrderSelectors.getOrderSubmittingPromoCode);
  readonly submittingPromoCodeError = this.selectSignal(OrderSelectors.getOrderSubmittingPromoCodeError);
  readonly orderTransaction = this.selectSignal(OrderSelectors.getOrderTransaction);
  readonly orderTransactionProcessing = this.selectSignal(OrderSelectors.getOrderTransactionProcessing);
  readonly orderTransactionError = this.selectSignal(OrderSelectors.getOrderTransactionError);

  loadOrderByToken(token: string): void {
    super.load(OrderActions.loadOrder({ token }));
  }

  loadCompletedOrderByNumber(orderNumber: string): void {
    super.load(OrderActions.loadCompletedOrder({ orderNumber }));
  }

  createOrder(payload: CreateOrderPayload): void {
    super.load(OrderActions.createOrder({ payload }));
  }

  getOrder(): Observable<Order | null> {
    return super.get(OrderSelectors.getOrder);
  }

  clearUpdatingTipError(): void {
    super.load(OrderActions.clearUpdatingTipError());
  }

  getOrderAfterItemsHaveBeenUpdated(): Observable<Order> {
    return this.getOrder()
      .pipe(
        filter((order): order is Order => !!order),
        distinctUntilChanged((previousOrder, currentOrder) => {
          const sameBookings = (previousOrder?.bookings?.length ?? 0) === (currentOrder?.bookings?.length ?? 0);
          const sameLineItems = (previousOrder?.line_items?.length ?? 0) === (currentOrder?.line_items?.length ?? 0);

          return (previousOrder?.id ?? -1) === currentOrder.id && sameBookings && sameLineItems;
        }),
      );
  }

  getOrderAfterItemsHaveBeenAdded(): Observable<Order> {
    return this.getOrder()
      .pipe(
        filter((order): order is Order => !!order),
        distinctUntilChanged<Order>((previousOrder, currentOrder) => {
          const bookingsAdded = (previousOrder?.bookings?.length ?? -1) < (currentOrder?.bookings?.length ?? -1);
          const lineItemsAdded = (previousOrder?.line_items?.length ?? -1) < (currentOrder?.line_items?.length ?? -1);

          return !bookingsAdded && !lineItemsAdded;
        }),
      );
  }

  getOrderError(): Observable<Error | null> {
    return super.get(OrderSelectors.getOrderError);
  }

  getCompletedOrder(): Observable<Order | null> {
    return super.get(OrderSelectors.getCompletedOrder);
  }

  getOrderTimeout(): Observable<number> {
    return super.get(OrderSelectors.getOrderTimeout);
  }

  /**
   * Order management
   */
  addItem(payload: {
    request: LineItemRequest | BookingRequest;
  }): void {
    super.load(OrderActions.addItem(payload));
  }

  removeItem(payload: {
    item: Booking|LineItem;
    order: Order;
  }): void {
    super.load(OrderActions.removeItem(payload));
  }

  getOrderIsItemBeingAdded(): Observable<boolean> {
    return super.get(OrderSelectors.getOrderIsItemBeingAdded);
  }

  getOrderListOfItemsBeingDeleted(): Observable<(number)[]> {
    return super.get(OrderSelectors.getOrderIsItemBeingRemoved);
  }

  getOrderIsItemBeingRemoved(item: LineItem | Booking): Observable<boolean> {
    return super.get<any, number[]>(OrderSelectors.getOrderIsItemBeingRemoved)
      .pipe(
        map(ids => ids.includes(item.id as number))
      );
  }

  getOrderErrorProcessingItem(): Observable<Error | null>  {
    return super.get(OrderSelectors.getOrderErrorProcessingItem);
  }

  getOrderLastAddedItem(): Observable<Booking | LineItem | null> {
    return super.get(OrderSelectors.getOrderLastAddedItem);
  }

  /**
   * Submit tip
   *
   * @param payload tip amount in cents
   */
  submitTip(payload: { tip: TipUpdatedEvent; order: Order }) {
    super.load(OrderActions.submitTip(payload));
  }


  getUpdatingTip(): Observable<boolean> {
    return super.get(OrderSelectors.getOrderUpdatingTip);
  }

  getUpdatingTipError(): Observable<Error | null> {
    return super.get(OrderSelectors.getOrderUpdatingTipError);
  }

  /**
   * Promo code processing
   */
  submitPromoCode(payload: {
    order: Order;
    code: string;
  }) {
    super.load(OrderActions.submitPromoCode(payload));
  }

  removePromoCode(payload: {
    order: Order;
  }) {
    super.load(OrderActions.removePromoCode(payload));
  }

  clearSubmittingPromoCode() {
    super.load(OrderActions.clearSubmittingPromoCode());
  }

  getSubmittingPromoCode(): Observable<boolean> {
    return super.get(OrderSelectors.getOrderSubmittingPromoCode);
  }

  getSubmittingPromoCodeError(): Observable<Error | null> {
    return super.get(OrderSelectors.getOrderSubmittingPromoCodeError);
  }

  /**
   * Transaction processing
   */
  beginCheckout() {
    this.load(OrderTransactionActions.beginCheckout());
  }

  checkout(payload: {
    card: stripe.elements.Element;
    customerDetails: CustomerDetails;
    marketingCommunications: boolean;
    order: Order;
  }) {
    const { card, customerDetails, order, marketingCommunications } = payload;
    /**
     * The reason we have to call stripe here and not on effects is
     * zone.js Promise handling error. Unable to fix it by using
     * ngZone.run, decided to go with that workaround in order not to
     * waste any more time struggling with zone.js errors
     */
    return this.stripeService
      .createToken(card, customerDetails)
      .pipe(
        map(res => {
          if (!res.token || !res.token.id) {
            const message = 'Unable to retrieve Stripe token';

            return OrderTransactionActions.checkoutFailure({ error: new Error(message) });
          }
          return OrderTransactionActions.checkout({
            token: res.token.id, customerDetails, order, marketingCommunications,
          });
        }),
        catchError(error => of(OrderTransactionActions.checkoutFailure({ error }))),
        take(1),
      )
      .subscribe(action => {
        super.load(action);
      });
  }

  getOrderTransaction(): Observable<Transaction | null> {
    return super.get(OrderSelectors.getOrderTransaction);
  }
  getOrderTransactionProcessing(): Observable<boolean> {
    return super.get(OrderSelectors.getOrderTransactionProcessing);
  }
  getOrderTransactionError(): Observable<Error | null> {
    return super.get(OrderSelectors.getOrderTransactionError);
  }
}
